import React, { useMemo } from "react";

import { useWarehouseContext } from "./WarehouseProvider";
import { useConfiguration } from "./useConfiguration";

import { CapturePackagePhotos } from "./CapturePackagePhotos";
import { StepHeader } from "./components/StepHeader";
import { CheckContents } from "./CheckContents";
import { clsx } from "clsx";
import { ControlWeightCapture } from "./ControlWeightCapture";
import { PackagingWeightCapture } from "./PackagingWeightCapture";
import { ContentWeightCapture } from "./ContentWeightCapture";
import { NonConformPhoto } from "./NonConformPhoto";
import { SortContents } from "./SortContents";
import { AssembledWeightCapture } from "./AssembledWeightCapture";
import { ConformingWeightCapture } from "./ConformingWeightCapture";
import { CaptureScaleReadingPhoto } from "./CaptureScaleReadingPhoto";
import { Summary } from "./Summary";

import { ReactComponent as WarningSvg } from "./svgs/warning.svg";
import { FullscreenModal } from "./components/FullscreenModal";

import { useLocale } from "./useLocale";
import { useSteps } from "./useSteps";
import { isStepVisible } from "./useConfiguration";

const WeightDisplay = ({ weight }) => {
  const locale = useLocale();
  const formatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: "decimal" }),
    [locale]
  );

  const formatWeight = (weight) => {
    return weight !== "" ? formatter.format(weight) : "-";
  };

  return weight ? (
    <span className="text-white">
      <span className="font-bold font-['Space_Mono']">
        {formatWeight(weight)}
      </span>
      <span className="text-xl ml-2 text-[#8086A0]">kg</span>
    </span>
  ) : (
    <div className="text-xl text-[#D8A924] relative bottom-0 flex flex-row gap-2 items-center">
      <WarningSvg style={{ width: "1rem" }} /> Weight not recorded
    </div>
  );
};

const PhotosDisplay = ({ photos, isOptional = false }) => {
  return photos.length > 0 ? (
    <div className="grid gap-2 grid-cols-6 lg:grid-cols-12">
      {photos.map((image, index) => (
        <div key={index} className="relative">
          <img
            src={image}
            alt={`Captured ${index}`}
            className="w-full h-[2.5rem] border border-[#282F44] rounded-lg"
          />
        </div>
      ))}
    </div>
  ) : isOptional ? (
    <span className="text-[#8086A0]">No photos taken (optional)</span>
  ) : (
    <div className="text-xl text-[#D8A924] relative bottom-0 flex flex-row gap-2 items-center w-full">
      <WarningSvg style={{ width: "1rem" }} /> Photos not taken
    </div>
  );
};

export const AllSteps = () => {
  const {
    packagePictures,
    controlWeight,
    packagingWeight,
    contentWeight,
    sortedPictures,
    nonConformPictures,
    assembledWeight,
    conformingWeight,
    scaleReadingPictures,
  } = useWarehouseContext();

  const { jumpTo, closeAllSteps } = useSteps();

  const { isStepValid } = useConfiguration();

  const stepsStatus = [
    {
      step: CapturePackagePhotos,
      valid: isStepValid(CapturePackagePhotos),
      value: <PhotosDisplay photos={packagePictures} />,
    },
    {
      step: CheckContents,
      valid: isStepValid(CheckContents),
      value: <span className="font-bold text-white">Accepted</span>,
    },
    {
      step: ControlWeightCapture,
      valid: isStepValid(ControlWeightCapture),
      value: <WeightDisplay weight={controlWeight} />,
    },
    {
      step: ContentWeightCapture,
      valid: isStepValid(ContentWeightCapture),
      value: <WeightDisplay weight={contentWeight} />,
    },
    {
      step: PackagingWeightCapture,
      valid: isStepValid(PackagingWeightCapture),
      value: <WeightDisplay weight={packagingWeight} />,
    },
    {
      step: SortContents,
      valid: isStepValid(SortContents),
      value: <PhotosDisplay photos={Object.values(sortedPictures)} />,
    },
    {
      step: NonConformPhoto,
      valid: isStepValid(NonConformPhoto),
      value: <PhotosDisplay photos={nonConformPictures} isOptional={true} />,
    },
    {
      step: AssembledWeightCapture,
      valid: isStepValid(AssembledWeightCapture),
      value: <WeightDisplay weight={assembledWeight} />,
    },
    {
      step: ConformingWeightCapture,
      valid: isStepValid(ConformingWeightCapture),
      value: <WeightDisplay weight={conformingWeight} />,
    },
    {
      step: CaptureScaleReadingPhoto,
      valid: isStepValid(CaptureScaleReadingPhoto),
      value: <PhotosDisplay photos={scaleReadingPictures} />,
    },
    { step: Summary, valid: isStepValid(Summary) },
  ];

  return (
    <FullscreenModal>
      <StepHeader showLogIssueIcon onExit={closeAllSteps} />
      <div className="absolute w-full flex flex-col">
        <ul className="h-full w-full p-0 flex flex-col mt-0.5">
          {stepsStatus
            .filter((row) => isStepVisible(row.step))
            .map((row, index, filteredArray) => {
              return (
                <div
                  key={`step-${index + 1}`}
                  onClick={() => {
                    jumpTo(row.step);
                  }}
                >
                  <li
                    className={clsx(
                      "relative flex flex-row items-center gap-8 m-0 p-4 border-b border-b-[#282F44] bg-[#030711]",
                      {
                        "border-t border-t-[#282F44]": index === 0,
                      }
                    )}
                  >
                    {index > 0 && (
                      <div className="absolute top-0 left-[2.45rem] w-1 h-1/2 border-l-2 border-l-[#112B6C]" />
                    )}
                    {index + 1 < filteredArray.length && (
                      <div className="absolute bottom-0 left-[2.45rem] w-1 h-1/2 border-l-2  border-l-[#112B6C]" />
                    )}
                    <div
                      className={clsx(
                        "z-10 rounded-full w-[1rem] h-[1rem] m-4 border-2 border-white",
                        {
                          "bg-[#D8A924]": !row.valid,
                          "bg-[#00CFA8]":
                            row.valid && index + 1 !== filteredArray.length,
                          "bg-[#173991]":
                            row.valid && index + 1 === filteredArray.length,
                        }
                      )}
                    />
                    <div className="flex flex-col">
                      <p className="text-white">{row.step.title}</p>
                      <div>{row.value}</div>
                    </div>
                  </li>
                </div>
              );
            })}
        </ul>
      </div>
    </FullscreenModal>
  );
};
